import { atom, SetStateAction } from 'jotai';

/**
 * Creates a Jotai atom that synchronizes its value across browser tabs
 * via the BroadcastChannel API.
 *
 * Drop-in replacement for `atom(initialValue)`. Local writes are broadcast
 * to other tabs; incoming messages from other tabs update the local value.
 *
 * @see https://jotai.org/docs/recipes/atom-with-broadcast
 */
export function atomWithBroadcast<Value>(key: string, initialValue: Value) {
  const baseAtom = atom(initialValue);

  // Gracefully degrade in environments without BroadcastChannel (SSR, jsdom tests).
  if (typeof BroadcastChannel === 'undefined') {
    return atom(
      (get) => get(baseAtom),
      (_get, set, update: SetStateAction<Value>) => {
        set(baseAtom, update);
      },
    );
  }

  const listeners = new Set<(event: MessageEvent<Value>) => void>();
  const channel = new BroadcastChannel(key);

  channel.onmessage = (event) => {
    listeners.forEach((l) => l(event));
  };

  const broadcastAtom = atom(
    (get) => get(baseAtom),
    (get, set, update: { isEvent: boolean; value: SetStateAction<Value> }) => {
      set(baseAtom, update.value);
      if (!update.isEvent) {
        channel.postMessage(get(baseAtom));
      }
    },
  );

  broadcastAtom.onMount = (setAtom) => {
    const listener = (event: MessageEvent<Value>) => {
      setAtom({ isEvent: true, value: event.data });
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const returnedAtom = atom(
    (get) => get(broadcastAtom),
    (_get, set, update: SetStateAction<Value>) => {
      set(broadcastAtom, { isEvent: false, value: update });
    },
  );

  return returnedAtom;
}
